import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  AppBar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Toolbar,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { getNavSections, getRouteInfo } from '../../config/drawer-route-config';
import { RoutesList } from '../../config/routes-list';
import { useQrScannerNavigate } from '../../hooks/qr-binding/use-qr-scanner-navigate';
import { useIsDesktop } from '../../hooks/use-is-desktop';
import { useNotificationBell, useUnreadCount } from '../../hooks/use-notifications';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from '../../hooks/use-notifications-mutations';
import {
  formatRelativeTime,
  getNotificationIcon,
} from '../../pages/notifications/notification-visuals';
import type { NotificationItem } from '../../services/notification/notification.service.interface';
import { colors } from '../../theme/ai-theme';
import { visuallyHidden } from '../../utils/visually-hidden';
import { QrScannerModal } from '../qr-scanner-modal';
import { TenantSelector } from './TenantSelector';

interface AppNavBarProps {
  className?: string;
  onMenuClick: () => void;
  drawerWidth: number;
}

function handleIntersectionEntries(
  entries: IntersectionObserverEntry[],
  notificationsRef: React.RefObject<NotificationItem[]>,
  markAsRead: (id: string) => void,
) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      const notificationId = entry.target.getAttribute('data-notification-id');
      if (notificationId) {
        const notification = notificationsRef.current.find((n) => n.id === notificationId);
        if (notification && !notification.isRead) {
          markAsRead(notificationId);
        }
      }
    }
  });
}

const POPOVER_WIDTH = { xs: 340, sm: 380 };

function NotificationList({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { items: notifications, unreadCount, isLoading, isError, refetch } = useNotificationBell();
  const { mutate: markAsRead } = useMarkNotificationRead();
  const { mutate: markAllAsRead, isPending: isMarkingAllRead } = useMarkAllNotificationsRead();
  const navigate = useNavigate();
  const listRef = React.useRef<HTMLUListElement>(null);
  const notificationsRef = React.useRef<NotificationItem[]>(notifications);
  notificationsRef.current = notifications;
  const visibleNotifications = notifications;

  const handleNotificationClick = (notification: NotificationItem) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
    onClose();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-observe DOM only when item count changes; notificationsRef/markAsRead stay fresh via ref and stable mutate identity
  React.useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        handleIntersectionEntries(entries, notificationsRef, markAsRead);
      },
      { root: listElement, threshold: 0.5 },
    );

    const items = listElement.querySelectorAll('[data-notification-id]');
    items.forEach((item) => {
      observer.observe(item);
    });

    return () => observer.disconnect();
  }, [notifications.length]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, width: POPOVER_WIDTH }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        role="alert"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          p: 3,
          width: POPOVER_WIDTH,
        }}
      >
        <ErrorOutlineOutlinedIcon sx={{ fontSize: 28, color: 'error.main' }} />
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
          {t('notifications.loadError.description')}
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => refetch()}>
          {t('notifications.loadError.retry')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: POPOVER_WIDTH, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {t('notifications.title')}
        </Typography>
        <Button
          size="small"
          onClick={() => markAllAsRead()}
          disabled={unreadCount === 0 || isMarkingAllRead}
          aria-label={t('notifications.markAllRead')}
          sx={{ minHeight: 44 }}
        >
          {t('notifications.markAllRead')}
        </Button>
      </Box>

      {visibleNotifications.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            p: 4,
          }}
        >
          <NotificationsOutlinedIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            {t('notifications.empty')}
          </Typography>
        </Box>
      ) : (
        <List ref={listRef} sx={{ maxHeight: 400, overflow: 'auto', p: 0 }}>
          {visibleNotifications.map((notification, index) => {
            const icon = getNotificationIcon(notification.type);

            return (
              <Box key={notification.id} data-notification-id={notification.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      gap: 1,
                      minHeight: 44,
                      alignItems: 'flex-start',
                      transition: 'background-color 150ms ease',
                      '@media (prefers-reduced-motion: reduce)': {
                        transition: 'none',
                      },
                    }}
                  >
                    <Box sx={{ pt: 0.75, minWidth: 8 }}>
                      {!notification.isRead && (
                        <Box
                          component="span"
                          sx={{
                            display: 'block',
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                          }}
                        >
                          <Box component="span" sx={visuallyHidden}>
                            {t('notifications.unread', 'Non letta')}
                          </Box>
                        </Box>
                      )}
                    </Box>
                    <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>{icon}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: notification.isRead ? 400 : 600 }}
                        >
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography
                            variant="body2"
                            component="span"
                            title={notification.body}
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              color: 'text.secondary',
                            }}
                          >
                            {notification.body}
                          </Typography>
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}
                          >
                            {formatRelativeTime(notification.createdAt)}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {index < visibleNotifications.length - 1 && <Divider />}
              </Box>
            );
          })}
        </List>
      )}

      <Box sx={{ borderTop: 1, borderColor: 'divider', p: 1 }}>
        <Button
          fullWidth
          size="small"
          onClick={() => {
            navigate(RoutesList.NOTIFICATIONS);
            onClose();
          }}
          sx={{ minHeight: 44 }}
        >
          {t('notifications.viewAll')}
        </Button>
      </Box>
    </Box>
  );
}

export default function AppNavBar({ className, onMenuClick, drawerWidth }: AppNavBarProps) {
  const isDesktop = useIsDesktop();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const unreadCount = useUnreadCount();
  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
  const [qrOpen, setQrOpen] = useState(false);
  const { handleDecode, isPending: isQrSubmitting } = useQrScannerNavigate({
    onClose: () => setQrOpen(false),
  });

  const navSections = getNavSections(t);
  const routeInfo = getRouteInfo(pathname, navSections);
  const pageTitle = routeInfo?.label ?? 'Prospero';
  const PageIcon = routeInfo?.icon;
  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotifAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotifAnchorEl(null);
  };

  return (
    <AppBar
      className={className}
      position="fixed"
      elevation={0}
      sx={{
        top: 0,
        width: isDesktop ? `calc(100% - ${drawerWidth}px)` : '100%',
        ml: isDesktop ? `${drawerWidth}px` : 0,
        borderBottom: `5px solid ${colors.primary.main}`,
        paddingTop: 'env(safe-area-inset-top, 0px)',
        backgroundClip: 'border-box',
      }}
    >
      <Toolbar>
        {!isDesktop && (
          <IconButton
            edge="start"
            aria-label={t('nav.openMenu')}
            onClick={onMenuClick}
            sx={{
              mr: 2,
              color: colors.navigation.text,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1, minWidth: 0 }}>
          {PageIcon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: colors.navigation.text,
                '& .MuiSvgIcon-root': {
                  fontSize: '1.5rem',
                },
              }}
            >
              {PageIcon}
            </Box>
          )}
          <Typography
            variant="h6"
            component="div"
            noWrap
            sx={{
              fontWeight: 700,
              color: colors.navigation.text,
            }}
          >
            {pageTitle}
          </Typography>
        </Box>
        <TenantSelector />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            color="inherit"
            onClick={() => setQrOpen(true)}
            aria-label={t('qrScanner:openScanner')}
            sx={{
              color: colors.navigation.text,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            <QrCodeScannerIcon />
          </IconButton>
          <IconButton
            color="inherit"
            onClick={handleNotificationOpen}
            aria-label={t('notifications.openBell')}
            sx={{
              color: colors.navigation.text,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            <Badge
              badgeContent={unreadCount > 99 ? '99+' : unreadCount}
              color="error"
              showZero={false}
            >
              <NotificationsOutlinedIcon />
            </Badge>
          </IconButton>
          <Popover
            open={Boolean(notifAnchorEl)}
            anchorEl={notifAnchorEl}
            onClose={handleNotificationClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <NotificationList onClose={handleNotificationClose} />
          </Popover>
        </Box>
      </Toolbar>
      <QrScannerModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        onDecode={handleDecode}
        title={t('qrScanner:scanFromHeader')}
        isSubmitting={isQrSubmitting}
        showPreview
      />
    </AppBar>
  );
}
